import React, { useEffect, useState } from "react";
import "./FormulaBar.css";

/**
 * FormulaBar
 * Excel-style top bar: name box (A1 / range label), the "fx" badge, a
 * value/formula display, and the data toolbar (Edit/Save, Sort, Filter,
 * and the Merge & Center split button).
 *
 * When `editing.editMode` is on, the value display becomes an input:
 * type a value and press Enter to update the selected cell (Excel-style
 * formula-bar editing). `editing` is null for readers without the
 * ExcelWebEdit capability, which renders the bar exactly as before.
 */
function FormulaBar({
  selectedCellLabel, selectionSummary, selectedCellText,
  canMerge, canUnmerge, onMergeCenter, onMergeCells, onUnmerge,
  filterEnabled, onToggleFilter, onCustomSort, sortActive,
  editing, onCommitAt, selectedRow, selectedCol, editTargetKey,
  onShowAllHistory,
}) {
  const [mergeMenuOpen, setMergeMenuOpen] = useState(false);
  const [draft, setDraft] = useState(selectedCellText);

  // The commit TARGET is pinned to the cell that was selected when typing
  // began. Without this, the blur triggered by clicking another cell would
  // commit the draft into the FRESHLY clicked cell (spraying the same value
  // across cells - or erasing them with a stale empty draft).
  // { row, col, original } - original enables Escape to revert live edits.
  const pinnedRef = React.useRef(null);

  const commitDraft = (text) => {
    const target = pinnedRef.current;
    pinnedRef.current = null;
    if (target && onCommitAt) onCommitAt(target.row, target.col, text);
    // Re-sync the draft with the CURRENTLY selected cell. When the commit is
    // triggered by clicking another cell, the selection-change render can
    // happen while the pin is still set (the re-seed effect below skips),
    // so without this the bar keeps showing the previous cell's text (QA #2).
    setDraft(selectedCellText);
  };

  // Re-seed the draft whenever the selected cell (or its value) changes -
  // but never while a typed draft is pending commit.
  useEffect(() => {
    if (!pinnedRef.current) setDraft(selectedCellText);
  }, [editTargetKey, selectedCellText]);

  // Close the merge dropdown on any outside click.
  useEffect(() => {
    if (!mergeMenuOpen) return undefined;
    const close = () => setMergeMenuOpen(false);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [mergeMenuOpen]);

  const isFormula = selectedCellText.startsWith("=");
  const inputClass = [
    "simple-excel-formula-input",
    selectionSummary ? "formula-bar-selection"
      : isFormula ? "formula-bar-formula" : "",
  ].join(" ").trim();

  const showMerge = onMergeCenter || onUnmerge;
  // Excel's Merge & Center is a toggle: if the selection is already
  // merged, the main button unmerges; otherwise it merges & centres.
  const mainAction = () => { if (canUnmerge) onUnmerge(); else onMergeCenter(); };
  const runItem = (fn) => { if (fn) fn(); setMergeMenuOpen(false); };

  const editActive = !!(editing && editing.editMode);

  return (
    <div className="simple-excel-formula-bar">
      <div className="simple-excel-name-box">{selectedCellLabel}</div>
      <div className="simple-excel-fx-btn" title="Formula">
        <em>f</em><sub>x</sub>
      </div>

      {editActive && !selectionSummary ? (
        <input
          type="text"
          className={`${inputClass} sev-edit-input`}
          data-allow-text-selection="true"
          value={draft}
          onChange={(e) => {
            // First keystroke pins the target cell (and its original value).
            if (!pinnedRef.current) {
              pinnedRef.current = { row: selectedRow, col: selectedCol, original: selectedCellText };
            }
            const text = e.target.value;
            setDraft(text);
            // LIVE commit: the cell mirrors the formula bar on every
            // keystroke, exactly like Excel.
            if (onCommitAt) onCommitAt(pinnedRef.current.row, pinnedRef.current.col, text);
          }}
          onBlur={() => commitDraft(draft)}
          data-sev-undo-passthrough="true"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitDraft(draft);
              // Return focus to the grid (like Excel) so follow-up shortcuts
              // (Ctrl+Z undo, arrows, typing) aren't trapped in this input.
              e.currentTarget.blur();
            } else if (e.key === "Escape") {
              // Revert the live edits back to the original value.
              const target = pinnedRef.current;
              pinnedRef.current = null;
              if (target && onCommitAt) onCommitAt(target.row, target.col, target.original);
              setDraft(selectedCellText);
            } else if (
              (e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey &&
              String(e.key).toLowerCase() === "z"
            ) {
              if (pinnedRef.current) {
                // Mid-edit Ctrl+Z: revert THIS cell's live keystrokes (Excel
                // behaviour) and swallow the event so the grid-level undo
                // doesn't also pop its stack.
                e.preventDefault();
                const target = pinnedRef.current;
                pinnedRef.current = null;
                if (onCommitAt) onCommitAt(target.row, target.col, target.original);
                setDraft(target.original);
                e.stopPropagation();
              }
              // No pending draft: DON'T stop propagation — the data attribute
              // above lets the grid-level Ctrl+Z listener handle it.
              return;
            }
            // Keep grid keyboard-navigation from hijacking arrows/typing.
            e.stopPropagation();
          }}
          title="Type a value and press Enter to update the selected cell"
        />
      ) : (
        <div className={inputClass} title={selectedCellText}>
          {selectionSummary ? `Selection: ${selectionSummary}` : selectedCellText}
        </div>
      )}

      {(editing || onShowAllHistory || onCustomSort || onToggleFilter) && (
        <div className="simple-excel-data-tools" role="group" aria-label="Edit, sort and filter">
          {editing && (
            <button
              type="button"
              className={`sev-tool-btn ${editActive ? "active" : ""}`}
              onClick={editActive ? editing.onCancel : editing.onToggleEdit}
              disabled={editing.editBlocked}
              title={
                editing.editBlocked
                  ? "Clear sort/filter to edit cell values"
                  : editActive
                    ? "Cancel editing - unsaved changes revert to their original values"
                    : "Edit cell values"
              }
            >
              <span className="material-icons">{editActive ? "close" : "edit"}</span>
              <span>{editActive ? "Cancel" : "Edit"}</span>
            </button>
          )}
          {editing && editActive && editing.canSave && (
            <button
              type="button"
              className="sev-tool-btn"
              onClick={editing.onSave}
              disabled={!editing.dirty || editing.saving}
              title={editing.dirty ? "Save changes to the server" : "No changes to save"}
            >
              <span className="material-icons">save</span>
              <span>{editing.saving ? "Saving..." : "Save"}</span>
            </button>
          )}
          {editing && editing.statusMsg && (
            <span className="sev-edit-status">{editing.statusMsg}</span>
          )}
          {onShowAllHistory && (
            <button
              type="button"
              className="sev-tool-btn"
              onClick={onShowAllHistory}
              title="All changes made to this workbook - who, when, and what"
            >
              <span className="material-icons">history</span><span>History</span>
            </button>
          )}
          {onCustomSort && (
            <button
              type="button"
              className={`sev-tool-btn ${sortActive ? "active" : ""}`}
              onClick={onCustomSort}
              title={sortActive ? "Sort applied · open Custom Sort" : "Custom (multi-level) sort"}
            >
              <span className="material-icons">sort</span><span>Sort</span>
            </button>
          )}
          {onToggleFilter && (
            <button
              type="button"
              className={`sev-tool-btn ${filterEnabled ? "active" : ""}`}
              onClick={onToggleFilter}
              title={filterEnabled ? "Turn filter off" : "Turn filter on"}
            >
              <span className="material-icons">filter_alt</span><span>Filter</span>
            </button>
          )}
        </div>
      )}

      {showMerge && (
        <div className="sev-merge" role="group" aria-label="Merge cells">
          <button
            type="button"
            className={`sev-merge-main ${canUnmerge ? "active" : ""}`}
            disabled={!canMerge && !canUnmerge}
            onClick={mainAction}
            title={canUnmerge ? "Unmerge cells" : "Merge the selected cells and centre the content"}
          >
            <span className="material-icons">{canUnmerge ? "call_split" : "call_merge"}</span>
            <span>Merge &amp; Center</span>
          </button>
          <button
            type="button"
            className="sev-merge-caret"
            aria-haspopup="true"
            aria-expanded={mergeMenuOpen}
            onClick={(e) => { e.stopPropagation(); setMergeMenuOpen((o) => !o); }}
            title="Merge options"
          >
            <span className="material-icons">arrow_drop_down</span>
          </button>
          {mergeMenuOpen && (
            <div className="sev-merge-menu" onClick={(e) => e.stopPropagation()}>
              <button type="button" className="sev-merge-item" disabled={!canMerge} onClick={() => runItem(onMergeCenter)}>
                <span className="material-icons">call_merge</span><span>Merge &amp; Center</span>
              </button>
              <button type="button" className="sev-merge-item" disabled={!canMerge} onClick={() => runItem(onMergeCells)}>
                <span className="material-icons">call_merge</span><span>Merge Cells</span>
              </button>
              <button type="button" className="sev-merge-item" disabled={!canUnmerge} onClick={() => runItem(onUnmerge)}>
                <span className="material-icons">call_split</span><span>Unmerge Cells</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default React.memo(FormulaBar);
